import { Injector, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, throwError, timer } from 'rxjs';
import { Endpoints } from '../utils/Endpoints';
import { LocalStorage } from '../utils/local-storage.service';
import jwt_decode from 'jwt-decode';
import { catchError, delay, retryWhen, scan, startWith, switchMap, takeUntil } from "rxjs/operators";
import { Constants } from '../utils/Constants';
import * as i0 from "@angular/core";
import * as i1 from "@angular/router";
import * as i2 from "@angular/common/http";
export class AuthService {
    constructor(router, injector, httpClient) {
        this.router = router;
        this.injector = injector;
        this.httpClient = httpClient;
        this.unsubscribe$ = new Subject();
        this.isAuthenticated = new BehaviorSubject(false);
        this.localStorage = this.injector.get(LocalStorage);
    }
    setIsAuthenticated(status) {
        this.isAuthenticated.next(status);
    }
    get isAuthenticatedStatus() {
        return this.isAuthenticated.asObservable();
    }
    login(loginForm) {
        return this.httpClient.post(Endpoints.LOGIN_API, loginForm);
    }
    logout() {
        const accessToken = this.localStorage.getItem('access_token');
        return this.httpClient.get(Endpoints.LOGOUT_API + accessToken)
            .subscribe(resp => {
            const examId = this.localStorage.getItem('exam_id');
            this.removeTokens();
            const navigationExtras = {
                state: { code: examId },
            };
            this.router.navigate(['/login'], navigationExtras);
            if (window.opener) {
                window.opener.location.reload();
                window.close();
            }
        });
    }
    getFromStorage(key) {
        return this.localStorage.getItem(key);
    }
    getAccountDetails() {
        const idToken = this.localStorage.getItem('id_token');
        const decodedDetails = jwt_decode(idToken);
        this.localStorage.setItem('user', decodedDetails[`given_name`]);
        this.localStorage.setItem('email_address', decodedDetails[`email`]);
    }
    storeTokens(data) {
        this.localStorage.setItem('access_token', data.access_token);
        this.localStorage.setItem('id_token', data.id_token);
        this.localStorage.setItem('refresh_token', data.refresh_token);
        this.setTokenExpiryDate();
    }
    removeTokens() {
        this.localStorage.removeItem('access_token');
        this.localStorage.removeItem('refresh_token');
        this.localStorage.removeItem('id_token');
        this.localStorage.removeItem('token_expiry');
        this.localStorage.removeItem('email_address');
        this.localStorage.removeItem('user');
        this.setIsAuthenticated(false);
    }
    refreshToken() {
        const request = {
            emailAddress: this.getLoggedInUser(),
            refreshToken: this.retrieveRefreshToken()
        };
        return this.httpClient.post(`${Endpoints.REFRESH_TOKEN_API}`, request).pipe(catchError(e => throwError('Error occured while refreshing token: ' + e.message)));
    }
    getLoggedInUser() {
        return this.localStorage.getItem('email_address');
    }
    retrieveRefreshToken() {
        return this.localStorage.getItem('refresh_token');
    }
    getTokenExpiryDate() {
        return this.localStorage.getItem('token_expiry');
    }
    // calculate remaining time until next token refresh relative to current time
    getTimeBeforeTokenRefresh() {
        const currentTime = new Date().getTime();
        const timeBeforeTokenRefresh = +this.getTokenExpiryDate() - currentTime - Constants.REFRESH_TOKEN_TIME_BEFORE_EXPIRY;
        return timeBeforeTokenRefresh;
    }
    setSessionTimer(milliseconds) {
        this.sessionTimer$ = new Subject();
        this.sessionTimer$.pipe(startWith(milliseconds), switchMap(ms => timer(ms)), takeUntil(this.unsubscribe$)).subscribe(() => {
            // call refresh token api after x milliseconds
            this.refreshToken().pipe(retryWhen(e => e.pipe(scan((errorCount, error) => {
                // logout and stop timer after 3 retries with error response
                if (errorCount > 1) {
                    this.logout();
                    throw error;
                }
                return errorCount + 1;
            }, 0), delay(3000))), // delay retry by 3 seconds
            takeUntil(this.unsubscribe$)).subscribe(data => {
                if (data[`body`]) {
                    this.updateTokens(data[`body`]);
                    this.sessionTimer$.next(Constants.REFRESH_TOKEN_TIMER); // reset timer
                }
                else {
                    console.log('Response status code: ' + data[`statusCode`]);
                }
            });
        });
    }
    updateTokens(data) {
        this.localStorage.setItem('access_token', data[`access_token`]);
        this.localStorage.setItem('id_token', data[`id_token`]);
        this.setTokenExpiryDate();
    }
    setTokenExpiryDate() {
        const tokenExpiryDate = new Date();
        tokenExpiryDate.setHours(tokenExpiryDate.getHours() + 1);
        this.localStorage.setItem('token_expiry', tokenExpiryDate.getTime());
    }
    isTokenExpired() {
        const tokenExpiryDate = this.getTokenExpiryDate();
        return !(+tokenExpiryDate > new Date().getTime());
    }
    isSessionTimerSet() {
        return this.sessionTimer$ !== null && this.sessionTimer$ !== undefined;
    }
    checkUserAuthentication() {
        const token = this.getFromStorage('access_token');
        if (token != null && !this.isTokenExpired()) {
            this.setIsAuthenticated(true);
            // set session timer if not yet initialized
            if (!this.isSessionTimerSet()) {
                this.setSessionTimer(this.getTimeBeforeTokenRefresh());
            }
            return true;
        }
        else if (token != null) {
            this.removeTokens();
        }
        return false;
    }
    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
AuthService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function AuthService_Factory() { return new AuthService(i0.ɵɵinject(i1.Router), i0.ɵɵinject(i0.INJECTOR), i0.ɵɵinject(i2.HttpClient)); }, token: AuthService, providedIn: "root" });
